import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import './SideNav.css';

const SideNav = ({ isMobileOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const toggleMenu = (name) => setExpandedMenu((cur) => (cur === name ? null : name));
  const location = useLocation();

  // Check permission against user.permissions loaded at login (same source as Home page)
  const hasPerm = (name) => user?.permissions?.some((p) => p.name === name) ?? false;

  const isActive = (path) => location.pathname === path;

  const hasAnySetupPermission =
    hasPerm('users.read') || hasPerm('roles.read') || hasPerm('permissions.read') ||
    hasPerm('menus.read') || hasPerm('products.read') || hasPerm('packs.read') ||
    hasPerm('pincodes.read');

  const hasAnyOnlinePermission =
    hasPerm('online.approve') || hasPerm('online.orders.read') ||
    hasPerm('online.orders.myorder') || hasPerm('online.referrals') ||
    hasPerm('online.reconcile') || hasPerm('online.offers');

  const hasAnyBankPermission =
    hasPerm('bank.transactions.upload') || hasPerm('online.reconcile');

  const hasAnyStocksPermission =
    hasPerm('stocks.locations') || hasPerm('stocks.my_stock') || hasPerm('stocks.product_flow');

  const hasAnyReportsPermission = hasPerm('reports.view');

  const handleLinkClick = () => {
    if (onClose && window.innerWidth <= 768) onClose();
  };

  return (
    <nav className={`side-nav ${isMobileOpen ? 'mobile-open' : ''}`}>
      <ul className="nav-menu">
        {/* Home */}
        <li className={`nav-item ${isActive('/home') ? 'active' : ''}`}>
          <Link to="/home" className="nav-link" onClick={handleLinkClick}>
            <span className="nav-icon">🏠</span>
            <span className="nav-text">Home</span>
          </Link>
        </li>

        {/* Sales Orders */}
        {hasPerm('salesorders.read') && (
          <li className={`nav-item ${isActive('/sales-orders') ? 'active' : ''}`}>
            <Link to="/sales-orders" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">📦</span>
              <span className="nav-text">Sales Orders</span>
            </Link>
          </li>
        )}

        {/* Purchase Orders */}
        {hasPerm('purchaseorders.read') && (
          <li className={`nav-item ${isActive('/purchase-orders') ? 'active' : ''}`}>
            <Link to="/purchase-orders" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">🛒</span>
              <span className="nav-text">Purchase Orders</span>
            </Link>
          </li>
        )}

        {/* Expenses */}
        {hasPerm('expenses.read') && (
          <li className={`nav-item ${isActive('/expenses') ? 'active' : ''}`}>
            <Link to="/expenses" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">💰</span>
              <span className="nav-text">Expenses</span>
            </Link>
          </li>
        )}

        {/* Leads */}
        {hasPerm('leads.read') && (
          <li className={`nav-item ${isActive('/leads') ? 'active' : ''}`}>
            <Link to="/leads" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">🎯</span>
              <span className="nav-text">Leads</span>
            </Link>
          </li>
        )}

        {/* Setup */}
        {hasAnySetupPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${expandedMenu === 'setup' ? 'expanded' : ''}`}
              onClick={() => toggleMenu('setup')}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">Setup</span>
              <span className={`expand-arrow ${expandedMenu === 'setup' ? 'rotated' : ''}`}>▼</span>
            </div>

            <ul className={`nav-submenu ${expandedMenu === 'setup' ? 'show' : ''}`}>
              {hasPerm('users.read') && (
                <li className={`nav-subitem ${isActive('/setup/users') ? 'active' : ''}`}>
                  <Link to="/setup/users" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">👤</span>
                    <span className="nav-subtext">Users</span>
                  </Link>
                </li>
              )}
              {hasPerm('roles.read') && (
                <li className={`nav-subitem ${isActive('/setup/roles') ? 'active' : ''}`}>
                  <Link to="/setup/roles" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎭</span>
                    <span className="nav-subtext">Roles</span>
                  </Link>
                </li>
              )}
              {hasPerm('permissions.read') && (
                <li className={`nav-subitem ${isActive('/setup/permissions') ? 'active' : ''}`}>
                  <Link to="/setup/permissions" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔐</span>
                    <span className="nav-subtext">Permissions</span>
                  </Link>
                </li>
              )}
              {hasPerm('menus.read') && (
                <li className={`nav-subitem ${isActive('/setup/menu-admin') ? 'active' : ''}`}>
                  <Link to="/setup/menu-admin" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🗂️</span>
                    <span className="nav-subtext">Menu Admin</span>
                  </Link>
                </li>
              )}
              {hasPerm('products.read') && (
                <li className={`nav-subitem ${isActive('/setup/products') ? 'active' : ''}`}>
                  <Link to="/setup/products" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📦</span>
                    <span className="nav-subtext">Products</span>
                  </Link>
                </li>
              )}
              {hasPerm('packs.read') && (
                <li className={`nav-subitem ${isActive('/setup/product-packs') ? 'active' : ''}`}>
                  <Link to="/setup/product-packs" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎁</span>
                    <span className="nav-subtext">Product Packs</span>
                  </Link>
                </li>
              )}
              {hasPerm('pincodes.read') && (
                <li className={`nav-subitem ${isActive('/setup/pin-codes') ? 'active' : ''}`}>
                  <Link to="/setup/pin-codes" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📍</span>
                    <span className="nav-subtext">Pin Codes</span>
                  </Link>
                </li>
              )}
              {hasPerm('products.read') && (
                <li className={`nav-subitem ${isActive('/setup/categories') ? 'active' : ''}`}>
                  <Link to="/setup/categories" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🗂️</span>
                    <span className="nav-subtext">Categories</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Online */}
        {hasAnyOnlinePermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${expandedMenu === 'online' ? 'expanded' : ''}`}
              onClick={() => toggleMenu('online')}
            >
              <span className="nav-icon">🌐</span>
              <span className="nav-text">Online</span>
              <span className={`expand-arrow ${expandedMenu === 'online' ? 'rotated' : ''}`}>▼</span>
            </div>

            <ul className={`nav-submenu ${expandedMenu === 'online' ? 'show' : ''}`}>
              {hasPerm('online.approve') && (
                <li className={`nav-subitem ${isActive('/online/approve-customers') ? 'active' : ''}`}>
                  <Link to="/online/approve-customers" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">✅</span>
                    <span className="nav-subtext">Approve Customer</span>
                  </Link>
                </li>
              )}
              {(hasPerm('online.orders.read') || hasPerm('online.orders.myorder')) && (
                <li className={`nav-subitem ${isActive('/online/orders') ? 'active' : ''}`}>
                  <Link to="/online/orders" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🛍️</span>
                    <span className="nav-subtext">Orders</span>
                  </Link>
                </li>
              )}
              {hasPerm('online.referrals') && (
                <li className={`nav-subitem ${isActive('/online/referrals') ? 'active' : ''}`}>
                  <Link to="/online/referrals" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🤝</span>
                    <span className="nav-subtext">Partners</span>
                  </Link>
                </li>
              )}
              {hasPerm('online.referrals') && (
                <li className={`nav-subitem ${isActive('/online/commission-payout') ? 'active' : ''}`}>
                  <Link to="/online/commission-payout" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">💳</span>
                    <span className="nav-subtext">Commission Payout</span>
                  </Link>
                </li>
              )}
              {hasPerm('online.offers') && (
                <li className={`nav-subitem ${isActive('/online/offers') ? 'active' : ''}`}>
                  <Link to="/online/offers" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🎁</span>
                    <span className="nav-subtext">Offers</span>
                  </Link>
                </li>
              )}
              {hasPerm('online.approve') && (
                <li className={`nav-subitem ${isActive('/online/shared-addresses') ? 'active' : ''}`}>
                  <Link to="/online/shared-addresses" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📍</span>
                    <span className="nav-subtext">Shared Addresses</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Bank */}
        {hasAnyBankPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${expandedMenu === 'bank' ? 'expanded' : ''}`}
              onClick={() => toggleMenu('bank')}
            >
              <span className="nav-icon">🏦</span>
              <span className="nav-text">Bank</span>
              <span className={`expand-arrow ${expandedMenu === 'bank' ? 'rotated' : ''}`}>▼</span>
            </div>
            <ul className={`nav-submenu ${expandedMenu === 'bank' ? 'show' : ''}`}>
              {hasPerm('bank.transactions.upload') && (
                <li className={`nav-subitem ${isActive('/bank/transactions') ? 'active' : ''}`}>
                  <Link to="/bank/transactions" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📂</span>
                    <span className="nav-subtext">Transactions</span>
                  </Link>
                </li>
              )}
              {hasPerm('online.reconcile') && (
                <li className={`nav-subitem ${isActive('/online/reconcile') ? 'active' : ''}`}>
                  <Link to="/online/reconcile" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔗</span>
                    <span className="nav-subtext">Reconcile</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Stocks */}
        {hasAnyStocksPermission && (
          <li className="nav-item">
            <div
              className={`nav-link expandable ${expandedMenu === 'stocks' ? 'expanded' : ''}`}
              onClick={() => toggleMenu('stocks')}
            >
              <span className="nav-icon">🏭</span>
              <span className="nav-text">Stocks</span>
              <span className={`expand-arrow ${expandedMenu === 'stocks' ? 'rotated' : ''}`}>▼</span>
            </div>

            <ul className={`nav-submenu ${expandedMenu === 'stocks' ? 'show' : ''}`}>
              {hasPerm('stocks.my_stock') && (
                <li className={`nav-subitem ${isActive('/stocks/my-stock') ? 'active' : ''}`}>
                  <Link to="/stocks/my-stock" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📦</span>
                    <span className="nav-subtext">My Stock</span>
                  </Link>
                </li>
              )}
              {hasPerm('stocks.locations') && (
                <li className={`nav-subitem ${isActive('/stocks/locations') ? 'active' : ''}`}>
                  <Link to="/stocks/locations" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">📍</span>
                    <span className="nav-subtext">Locations</span>
                  </Link>
                </li>
              )}
              {hasPerm('stocks.product_flow') && (
                <li className={`nav-subitem ${isActive('/stocks/product-flow') ? 'active' : ''}`}>
                  <Link to="/stocks/product-flow" className="nav-sublink" onClick={handleLinkClick}>
                    <span className="nav-subicon">🔀</span>
                    <span className="nav-subtext">Product Flow</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* Reports */}
        {hasAnyReportsPermission && (
          <li className={`nav-item ${isActive('/reports') ? 'active' : ''}`}>
            <Link to="/reports" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">📊</span>
              <span className="nav-text">Reports</span>
            </Link>
          </li>
        )}

        {/* My Tasks */}
        {hasPerm('employee') && (
          <li className={`nav-item ${isActive('/my-tasks') ? 'active' : ''}`}>
            <Link to="/my-tasks" className="nav-link" onClick={handleLinkClick}>
              <span className="nav-icon">✅</span>
              <span className="nav-text">My Tasks</span>
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default SideNav;
